import { noMonitor, monitoredRequest } from "./api";

// Types for WhatsApp API
export interface WhatsAppMessage {
  id: string;
  from: string;
  to: string;
  timestamp: number;
  type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'contacts' | 'interactive' | 'sticker' | 'reaction' | 'system';
  direction: 'incoming' | 'outgoing';
  status: 'sent' | 'delivered' | 'read' | 'failed';
  content?: any;
  mediaUrl?: string;
  mediaType?: string;
  caption?: string;
  location?: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  };
  contacts?: any[];
  interactive?: any;
  reaction?: {
    messageId: string;
    emoji: string;
  };
  context?: {
    messageId: string;
  };
  error?: {
    code: number;
    message: string;
  };
}

export interface WhatsAppConversation {
  id: string;
  businessPhoneNumberId: string;
  participantPhone: string;
  contactName?: string;
  contactProfilePicture?: string;
  startTime: Date;
  lastMessageTime: Date;
  lastMessage?: string;
  status: 'active' | 'closed' | 'archived';
  messageCount: number;
  unreadCount: number;
  assignedTo?: string;
  tags?: string[];
  notes?: string;
}

export interface ConversationFilters {
  page?: number;
  limit?: number;
  status?: 'active' | 'closed' | 'archived' | 'all';
  assignedTo?: string;
  search?: string;
}

export interface MessageFilters {
  page?: number;
  limit?: number;
  before?: number;
  after?: number;
}

export interface SendTextMessageRequest {
  to: string;
  message: string;
  businessPhoneNumberId: string;
}

export interface SendMediaMessageRequest {
  to: string;
  mediaUrl: string;
  mediaType: 'image' | 'video' | 'audio' | 'document';
  caption?: string;
  businessPhoneNumberId: string;
}

export interface SendTemplateMessageRequest {
  to: string;
  templateName: string;
  templateLanguage: string;
  parameters?: any[];
  businessPhoneNumberId: string;
}

export interface SendInteractiveMessageRequest {
  to: string;
  interactiveType: 'button' | 'list';
  body: string;
  action: any;
  businessPhoneNumberId: string;
}

// ============================================================================
// CONVERSATION MANAGEMENT ENDPOINTS
// ============================================================================

/**
 * Get WhatsApp conversations for a business phone number
 * @param businessPhoneNumberId - The WhatsApp Business phone number ID
 * @param filters - Optional filters for conversations
 */
export const getConversations = async (
  businessPhoneNumberId: string,
  filters: ConversationFilters = {}
) => {
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required to fetch conversations.");
  }

  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.append('page', filters.page.toString());
  if (filters.limit) queryParams.append('limit', filters.limit.toString());
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.assignedTo) queryParams.append('assignedTo', filters.assignedTo);
  if (filters.search) queryParams.append('search', filters.search);

  const url = `/whatsapp/${businessPhoneNumberId}/conversations${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  console.log('📱 getConversations API call:', { businessPhoneNumberId, filters, url });
  
  return await noMonitor.get(url).then(res => {
    console.log('📱 getConversations API response:', { 
      status: res.status, 
      data: res.data,
      dataStructure: {
        hasData: !!res.data,
        hasDataData: !!res.data?.data,
        hasConversations: !!res.data?.data?.conversations,
        conversationsLength: res.data?.data?.conversations?.length
      }
    });
    
    // Handle different response structures
    if (res.data?.data?.conversations) {
      return res.data.data.conversations;
    } else if (res.data?.conversations) {
      return res.data.conversations;
    } else if (Array.isArray(res.data?.data)) {
      return res.data.data;
    } else if (Array.isArray(res.data)) {
      return res.data;
    } else {
      console.error('📱 Unexpected response structure:', res.data);
      return [];
    }
  }).catch(error => {
    console.error('📱 getConversations API error:', error);
    throw error;
  });
};

/**
 * Get a single conversation by ID
 * @param conversationId - The conversation ID
 */
export const getConversationById = async (conversationId: string) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to fetch conversation.");
  }
  return await monitoredRequest.get(`/whatsapp/conversation/${conversationId}`);
};

/**
 * Get messages for a specific conversation
 * @param conversationId - The conversation ID
 * @param filters - Optional filters for messages
 */
export const getConversationMessages = async (
  conversationId: string,
  filters: MessageFilters = {}
) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to fetch messages.");
  }

  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.append('page', filters.page.toString());
  if (filters.limit) queryParams.append('limit', filters.limit.toString());
  if (filters.before) queryParams.append('before', filters.before.toString());
  if (filters.after) queryParams.append('after', filters.after.toString());

  const url = `/whatsapp/conversation/${conversationId}/messages${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return await monitoredRequest.get(url);
};

/**
 * Get conversation statistics for a business phone number
 * @param businessPhoneNumberId - The WhatsApp Business phone number ID
 */
export const getConversationStats = async (businessPhoneNumberId: string) => {
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required to fetch stats.");
  }
  return await monitoredRequest.get(`/whatsapp/${businessPhoneNumberId}/stats`);
};

/**
 * Update conversation details
 * @param conversationId - The conversation ID
 * @param updates - Object containing fields to update
 */
export const updateConversation = async (
  conversationId: string,
  updates: Partial<{
    status: 'active' | 'closed' | 'archived';
    assignedTo: string;
    tags: string[];
    contactInfo: any;
  }>
) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to update conversation.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}`, updates);
};

// ============================================================================
// CONVERSATION ACTIONS
// ============================================================================

/**
 * Mark conversation as read
 * @param conversationId - The conversation ID
 */
export const markConversationAsRead = async (conversationId: string) => {
  console.log('Marking conversation as read:', conversationId);
  if (!conversationId) {
    throw new Error("Conversation ID is required to mark as read.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}/read`);
};

/**
 * Archive conversation
 * @param conversationId - The conversation ID
 */
export const archiveConversation = async (conversationId: string) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to archive conversation.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}/archive`);
};

/**
 * Close conversation
 * @param conversationId - The conversation ID
 */
export const closeConversation = async (conversationId: string) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to close conversation.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}/close`);
};

/**
 * Reopen conversation
 * @param conversationId - The conversation ID
 */
export const reopenConversation = async (conversationId: string) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to reopen conversation.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}/reopen`);
};

/**
 * Assign conversation to a user
 * @param conversationId - The conversation ID
 * @param userId - The user ID to assign to
 */
export const assignConversation = async (conversationId: string, userId: string) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to assign conversation.");
  }
  if (!userId) {
    throw new Error("User ID is required to assign conversation.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}/assign`, { userId });
};

/**
 * Toggle chatbot routing for a conversation
 * @param conversationId - The conversation ID
 * @param enabled - Whether to enable or disable chatbot routing
 */
export const toggleChatbotRouting = async (conversationId: string, enabled: boolean) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to toggle chatbot routing.");
  }
  if (typeof enabled !== 'boolean') {
    throw new Error("enabled must be a boolean value.");
  }
  return await monitoredRequest.patch(`/whatsapp/conversation/${conversationId}/chatbot-routing`, { enabled });
};

/**
 * Add internal note to conversation
 * @param conversationId - The conversation ID
 * @param note - The note content
 * @param authorId - The author's user ID
 */
export const addConversationNote = async (
  conversationId: string,
  note: string,
  authorId: string
) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required to add note.");
  }
  if (!note) {
    throw new Error("Note content is required.");
  }
  if (!authorId) {
    throw new Error("Author ID is required to add note.");
  }
  return await monitoredRequest.post(`/whatsapp/conversation/${conversationId}/notes`, {
    note,
    authorId
  });
};

// ============================================================================
// MESSAGE SENDING ENDPOINTS
// ============================================================================

/**
 * Send text message via WhatsApp
 * @param request - Text message request details
 */
export const sendTextMessage = async (request: SendTextMessageRequest) => {
  if (!request.to) {
    throw new Error("Recipient phone number is required.");
  }
  if (!request.message) {
    throw new Error("Message content is required.");
  }
  if (!request.businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }

  return await monitoredRequest.post('/whatsapp/send/text', request);
};

/**
 * Send media message via WhatsApp
 * @param request - Media message request details
 */
export const sendMediaMessage = async (request: SendMediaMessageRequest) => {
  if (!request.to) {
    throw new Error("Recipient phone number is required.");
  }
  if (!request.mediaUrl) {
    throw new Error("Media URL is required.");
  }
  if (!request.mediaType) {
    throw new Error("Media type is required.");
  }
  if (!request.businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }

  return await monitoredRequest.post('/whatsapp/send/media', request);
};

/**
 * Send template message via WhatsApp
 * @param request - Template message request details
 */
export const sendTemplateMessage = async (request: SendTemplateMessageRequest) => {
  if (!request.to) {
    throw new Error("Recipient phone number is required.");
  }
  if (!request.templateName) {
    throw new Error("Template name is required.");
  }
  if (!request.templateLanguage) {
    throw new Error("Template language is required.");
  }
  if (!request.businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }

  return await monitoredRequest.post('/whatsapp/send/template', request);
};

/**
 * Send interactive message via WhatsApp
 * @param request - Interactive message request details
 */
export const sendInteractiveMessage = async (request: SendInteractiveMessageRequest) => {
  if (!request.to) {
    throw new Error("Recipient phone number is required.");
  }
  if (!request.interactiveType) {
    throw new Error("Interactive type is required.");
  }
  if (!request.body) {
    throw new Error("Message body is required.");
  }
  if (!request.action) {
    throw new Error("Interactive action is required.");
  }
  if (!request.businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }

  return await monitoredRequest.post('/whatsapp/send/interactive', request);
};

// ============================================================================
// WEBHOOK ENDPOINTS
// ============================================================================

/**
 * Verify WhatsApp webhook
 * @param hubMode - The hub mode from WhatsApp
 * @param hubVerifyToken - The verification token
 * @param hubChallenge - The challenge string
 */
export const verifyWebhook = async (
  hubMode: string,
  hubVerifyToken: string,
  hubChallenge: string
) => {
  if (!hubMode || !hubVerifyToken || !hubChallenge) {
    throw new Error("Missing webhook verification parameters.");
  }

  return await noMonitor.get('/webhook/whatsapp', {
    params: {
      'hub.mode': hubMode,
      'hub.verify_token': hubVerifyToken,
      'hub.challenge': hubChallenge
    }
  });
};

/**
 * Handle WhatsApp webhook
 * @param webhookData - The webhook payload from WhatsApp
 */
export const handleWebhook = async (webhookData: any) => {
  if (!webhookData) {
    throw new Error("Webhook data is required.");
  }

  return await noMonitor.post('/webhook/whatsapp', webhookData);
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get user's available WhatsApp phone numbers
 */
export const getWhatsAppPhoneNumbers = async () => {
  return await monitoredRequest.get('/whatsapp/phone-numbers');
};

/**
 * Get WhatsApp Business Account information
 * @param businessPhoneNumberId - The business phone number ID
 */
export const getBusinessAccountInfo = async (businessPhoneNumberId: string) => {
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }
  return await monitoredRequest.get(`/whatsapp/${businessPhoneNumberId}/info`);
};

/**
 * Get message templates for a business account
 * @param businessPhoneNumberId - The business phone number ID
 */
export const getMessageTemplates = async (businessPhoneNumberId: string) => {
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }
  return await monitoredRequest.get(`/whatsapp/${businessPhoneNumberId}/templates`);
};

/**
 * Search conversations across all business phone numbers
 * @param query - The search query
 * @param filters - Additional search filters
 */
export const searchConversations = async (
  query: string,
  filters: {
    businessPhoneNumberIds?: string[];
    dateFrom?: Date;
    dateTo?: Date;
    status?: string;
    assignedTo?: string;
  } = {}
) => {
  if (!query) {
    throw new Error("Search query is required.");
  }

  const requestBody = {
    query,
    ...filters
  };

  return await monitoredRequest.post('/whatsapp/search/conversations', requestBody);
};

/**
 * Search messages across conversations
 * @param query - The search query
 * @param filters - Additional search filters
 */
export const searchMessages = async (
  query: string,
  filters: {
    conversationId?: string;
    businessPhoneNumberId?: string;
    dateFrom?: Date;
    dateTo?: Date;
    messageType?: string;
  } = {}
) => {
  if (!query) {
    throw new Error("Search query is required.");
  }

  const requestBody = {
    query,
    ...filters
  };

  return await monitoredRequest.post('/whatsapp/search/messages', requestBody);
};

/**
 * Bulk send messages to multiple recipients
 * @param requests - Array of message requests
 */
export const bulkSendMessages = async (requests: SendTextMessageRequest[]) => {
  if (!requests || requests.length === 0) {
    throw new Error("Message requests are required for bulk sending.");
  }

  // Validate each request
  requests.forEach((request, index) => {
    if (!request.to) {
      throw new Error(`Recipient phone number is required for request at index ${index}.`);
    }
    if (!request.message) {
      throw new Error(`Message content is required for request at index ${index}.`);
    }
    if (!request.businessPhoneNumberId) {
      throw new Error(`Business phone number ID is required for request at index ${index}.`);
    }
  });

  return await monitoredRequest.post('/whatsapp/send/bulk', { messages: requests });
};

/**
 * Upload media file for WhatsApp messaging
 * @param file - The file to upload
 * @param businessPhoneNumberId - The business phone number ID
 */
export const uploadMediaFile = async (file: File | Blob, businessPhoneNumberId: string) => {
  if (!file) {
    throw new Error("File is required for upload.");
  }
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('businessPhoneNumberId', businessPhoneNumberId);

  return await monitoredRequest.post('/whatsapp/media/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Get media file from WhatsApp
 * @param mediaId - The media ID from WhatsApp
 * @param businessPhoneNumberId - The business phone number ID
 */
export const getMediaFile = async (mediaId: string, businessPhoneNumberId: string) => {
  if (!mediaId) {
    throw new Error("Media ID is required.");
  }
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }

  return await monitoredRequest.get(`/whatsapp/media/${mediaId}`, {
    params: { businessPhoneNumberId }
  });
};

// ============================================================================
// RATE LIMITING AND ERROR HANDLING
// ============================================================================

/**
 * Check rate limit status for a business phone number
 * @param businessPhoneNumberId - The business phone number ID
 */
export const getRateLimitStatus = async (businessPhoneNumberId: string) => {
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }
  return await monitoredRequest.get(`/whatsapp/${businessPhoneNumberId}/rate-limit`);
};

/**
 * Get webhook delivery status and retry failed webhooks
 * @param businessPhoneNumberId - The business phone number ID
 */
export const getWebhookStatus = async (businessPhoneNumberId: string) => {
  if (!businessPhoneNumberId) {
    throw new Error("Business phone number ID is required.");
  }
  return await monitoredRequest.get(`/whatsapp/${businessPhoneNumberId}/webhook-status`);
};

/**
 * Retry failed webhook delivery
 * @param webhookId - The webhook ID to retry
 */
export const retryWebhook = async (webhookId: string) => {
  if (!webhookId) {
    throw new Error("Webhook ID is required.");
  }
  return await monitoredRequest.post(`/whatsapp/webhook/${webhookId}/retry`);
};

// ============================================================================
// AVATAR MANAGEMENT
// ============================================================================

/**
 * Get avatar URL for a WhatsApp contact
 * @param phoneNumber - Phone number in E.164 format
 * @param options - Options for avatar retrieval
 * @returns Avatar URL or null
 */
export const getWhatsAppAvatar = async (phoneNumber: string, options: any = {}) => {
  try {
    console.log('📸 Getting WhatsApp avatar for:', phoneNumber);
    const response = await monitoredRequest.post('/whatsapp/avatar', {
      phoneNumber,
      options
    });
    console.log('📸 WhatsApp avatar response:', response.data);
    return response.data.data.avatarUrl;
  } catch (error) {
    console.error('📸 Error getting WhatsApp avatar:', error);
    throw error;
  }
};

/**
 * Get avatars for multiple WhatsApp contacts
 * @param phoneNumbers - Array of phone numbers
 * @param options - Options for avatar retrieval
 * @returns Object with phone numbers as keys and avatar URLs as values
 */
export const getBatchWhatsAppAvatars = async (phoneNumbers: string[], options: any = {}) => {
  try {
    console.log('📸 Getting batch WhatsApp avatars for:', phoneNumbers.length, 'contacts');
    const response = await monitoredRequest.post('/whatsapp/avatars/batch', {
      phoneNumbers,
      options
    });
    console.log('📸 Batch WhatsApp avatars response:', response.data);
    return response.data.data.avatars;
  } catch (error) {
    console.error('📸 Error getting batch WhatsApp avatars:', error);
    throw error;
  }
};

/**
 * Update WhatsApp avatar settings
 * @param settings - Avatar settings
 */
export const updateWhatsAppAvatarSettings = async (settings: {
  useThirdPartyAvatars?: boolean;
  thirdPartyApiKey?: string;
}) => {
  try {
    console.log('📸 Updating WhatsApp avatar settings:', settings);
    const response = await monitoredRequest.put('/whatsapp/avatar/settings', settings);
    console.log('📸 WhatsApp avatar settings updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('📸 Error updating WhatsApp avatar settings:', error);
    throw error;
  }
};

// Export all functions as default object for easy import
export default {
  // Conversation Management
  getConversations,
  getConversationById,
  getConversationMessages,
  getConversationStats,
  updateConversation,

  // Conversation Actions
  markConversationAsRead,
  archiveConversation,
  closeConversation,
  reopenConversation,
  assignConversation,
  addConversationNote,
  toggleChatbotRouting,

  // Message Sending
  sendTextMessage,
  sendMediaMessage,
  sendTemplateMessage,
  sendInteractiveMessage,
  bulkSendMessages,

  // Media Management
  uploadMediaFile,
  getMediaFile,

  // Webhook Management
  verifyWebhook,
  handleWebhook,
  getWebhookStatus,
  retryWebhook,

  // Utility Functions
  getWhatsAppPhoneNumbers,
  getBusinessAccountInfo,
  getMessageTemplates,
  searchConversations,
  searchMessages,
  getRateLimitStatus,

  // Avatar Management
  getWhatsAppAvatar,
  getBatchWhatsAppAvatars,
  updateWhatsAppAvatarSettings
};